import React, { useState, useEffect } from 'react';
import { 
  getTaxonomy,
  defaultTaxonomy 
} from '../lib/productTaxonomy';
import {
  getAllDimensionConfigs,
  saveDimensionConfig,
  deleteDimensionConfig,
  dimensionTemplates,
  applyDimensionTemplate,
  getAllDimensionTemplates,
  saveDimensionTemplate,
  deleteDimensionTemplate,
  createTemplateFromConfig
} from '../lib/dimensionConfig';

const DimensionConfigSettings = () => {
  const [taxonomy, setTaxonomy] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPath, setSelectedPath] = useState('');
  const [editingConfig, setEditingConfig] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState({});
  const [templates, setTemplates] = useState({});
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateKey, setNewTemplateKey] = useState('');
  const [creatingNewTemplate, setCreatingNewTemplate] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [taxonomyData, configsData, templatesData] = await Promise.all([
        getTaxonomy(),
        getAllDimensionConfigs(),
        getAllDimensionTemplates()
      ]);
      setTaxonomy(taxonomyData);
      setConfigs(configsData);
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildTaxonomyPath = (keys) => {
    return keys.join('.');
  };

  const toggleExpanded = (path) => {
    setExpandedPaths(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const handleSelectPath = (path) => {
    setSelectedPath(path);
    const existingConfig = configs.find(c => c.path === path);
    
    if (existingConfig) {
      setEditingConfig(existingConfig);
    } else {
      setEditingConfig({
        path: path,
        name: path.split('.').pop().replace(/_/g, ' '),
        dimensions: {}
      });
    }
    setShowTemplates(false);
  };

  const handleAddDimension = () => {
    const newKey = `dimension_${Date.now()}`;
    setEditingConfig(prev => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [newKey]: {
          label: 'New Dimension',
          required: false,
          unit: 'cm',
          min: 0,
          max: 100,
          placeholder: '',
          description: ''
        }
      }
    }));
  };

  const handleUpdateDimension = (key, field, value) => {
    setEditingConfig(prev => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [key]: {
          ...prev.dimensions[key],
          [field]: field === 'required' ? value === 'true' : 
                   field === 'min' || field === 'max' ? parseFloat(value) || 0 : 
                   value
        }
      }
    }));
  };

  const handleRemoveDimension = (key) => {
    setEditingConfig(prev => {
      const newDimensions = { ...prev.dimensions };
      delete newDimensions[key];
      return {
        ...prev,
        dimensions: newDimensions
      };
    });
  };

  const handleSaveConfig = async () => {
    if (!editingConfig || !selectedPath) return;
    
    try {
      await saveDimensionConfig(selectedPath, editingConfig);
      await loadData();
      alert('Dimension configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error saving configuration: ' + error.message);
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this dimension configuration?')) {
      return;
    }
    
    try {
      await deleteDimensionConfig(configId);
      await loadData();
      setEditingConfig(null);
      setSelectedPath('');
    } catch (error) {
      console.error('Error deleting config:', error);
      alert('Error deleting configuration: ' + error.message);
    }
  };

  const handleApplyTemplate = async (templateKey) => {
    if (!selectedPath) return;
    
    try {
      await applyDimensionTemplate(selectedPath, templateKey);
      await loadData();
      const template = templates[templateKey];
      setEditingConfig({
        path: selectedPath,
        name: template.name,
        dimensions: template.dimensions,
        templateKey: templateKey
      });
      setShowTemplates(false);
      alert('Template applied successfully!');
    } catch (error) {
      console.error('Error applying template:', error);
      alert('Error applying template: ' + error.message);
    }
  };

  // Template Management Functions
  const handleCreateTemplate = async () => {
    if (!newTemplateKey || !newTemplateName || !editingConfig) return;
    
    try {
      await createTemplateFromConfig(newTemplateKey, newTemplateName, editingConfig);
      await loadData();
      setNewTemplateKey('');
      setNewTemplateName('');
      alert('Template created successfully!');
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Error creating template: ' + error.message);
    }
  };

  const handleStartNewTemplate = () => {
    const newKey = `custom_template_${Date.now()}`;
    setEditingTemplate({
      key: newKey,
      name: 'New Template',
      dimensions: {}
    });
    setCreatingNewTemplate(true);
  };

  const handleSaveNewTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await saveDimensionTemplate(editingTemplate.key, {
        name: editingTemplate.name,
        dimensions: editingTemplate.dimensions
      });
      await loadData();
      setEditingTemplate(null);
      setCreatingNewTemplate(false);
      alert('New template created successfully!');
    } catch (error) {
      console.error('Error creating new template:', error);
      alert('Error creating new template: ' + error.message);
    }
  };

  const handleCancelNewTemplate = () => {
    setEditingTemplate(null);
    setCreatingNewTemplate(false);
  };

  const handleEditTemplate = (templateKey) => {
    const template = templates[templateKey];
    setEditingTemplate({
      key: templateKey,
      name: template.name,
      dimensions: { ...template.dimensions }
    });
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await saveDimensionTemplate(editingTemplate.key, {
        name: editingTemplate.name,
        dimensions: editingTemplate.dimensions
      });
      await loadData();
      setEditingTemplate(null);
      alert('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template: ' + error.message);
    }
  };

  const handleDeleteTemplate = async (templateKey) => {
    const template = templates[templateKey];
    
    if (!template.isUserCreated) {
      alert('Cannot delete built-in templates');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the template "${template.name}"?`)) {
      return;
    }
    
    try {
      await deleteDimensionTemplate(templateKey);
      await loadData();
      alert('Template deleted successfully!');
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template: ' + error.message);
    }
  };

  const handleUpdateTemplateDimension = (key, field, value) => {
    setEditingTemplate(prev => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [key]: {
          ...prev.dimensions[key],
          [field]: field === 'required' ? value === 'true' : 
                   field === 'min' || field === 'max' ? parseFloat(value) || 0 : 
                   value
        }
      }
    }));
  };

  const handleAddTemplateDimension = () => {
    const newKey = `dimension_${Date.now()}`;
    setEditingTemplate(prev => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [newKey]: {
          label: 'New Dimension',
          required: false,
          unit: 'cm',
          min: 0,
          max: 100,
          placeholder: '',
          description: ''
        }
      }
    }));
  };

  const handleRemoveTemplateDimension = (key) => {
    setEditingTemplate(prev => {
      const newDimensions = { ...prev.dimensions };
      delete newDimensions[key];
      return {
        ...prev,
        dimensions: newDimensions
      };
    });
  };

  const renderTaxonomyTree = (node, path = [], level = 0) => {
    if (!node || typeof node !== 'object') return null;

    return Object.entries(node).map(([key, value]) => {
      if (key === 'metadata' || key === 'label' || key === 'description' || key === 'type' || key === 'image') {
        return null;
      }

      const currentPath = [...path, key];
      const pathString = buildTaxonomyPath(currentPath);
      const hasChildren = value && typeof value === 'object' && 
        Object.keys(value).some(k => k !== 'label' && k !== 'description' && k !== 'type' && k !== 'image' && typeof value[k] === 'object');
      const isExpanded = expandedPaths[pathString];
      const hasConfig = configs.some(c => c.path === pathString);
      const isSelected = selectedPath === pathString;

      return (
        <div key={key} className={`ml-${level * 4}`}>
          <div 
            className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-gray-100 ${
              isSelected ? 'bg-pink-100 border-l-4 border-pink-500' : ''
            }`}
            onClick={() => handleSelectPath(pathString)}
          >
            <div className="flex items-center flex-1">
              {hasChildren && (
                <button
                  className="mr-2 text-gray-500 hover:text-gray-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(pathString);
                  }}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              )}
              <span className="font-medium">{value.label || key}</span>
              {hasConfig && (
                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded">
                  Has Dimensions
                </span>
              )}
            </div>
          </div>

          {hasChildren && isExpanded && (
            <div className="ml-4">
              {renderTaxonomyTree(value, currentPath, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
        <span className="ml-2 text-gray-600">Loading dimension configurations...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              📏 Dimension Configuration
            </h2>
            <p className="text-gray-600">
              Manage dimension templates that can be applied to products in your taxonomy
            </p>
          </div>
          <button
            onClick={() => setShowTemplateManager(!showTemplateManager)}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            {showTemplateManager ? 'Hide Template Manager' : 'Manage Templates'}
          </button>
        </div>
      </div>

      {/* Information Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-blue-900">
              Dimension Configuration Has Moved!
            </h3>
            <div className="mt-2 text-sm text-blue-800">
              <p className="mb-2">
                Product dimension configuration is now integrated directly into the <strong>Product Taxonomy Management</strong> section. This provides a more streamlined workflow where you can:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Configure dimensions right alongside your product categories</li>
                <li>See dimension inheritance down the taxonomy hierarchy</li>
                <li>Apply templates directly to taxonomy items</li>
                <li>View dimension indicators in the taxonomy tree</li>
              </ul>
              <p className="mt-3">
                <strong>This section now focuses exclusively on managing dimension templates.</strong>
              </p>
            </div>
          </div>
        </div>
      </div>


      {/* Template Manager */}
      {showTemplateManager && (
        <div className="mt-8 bg-white rounded-lg shadow border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            🔧 Template Manager
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Template List */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-gray-700">Available Templates</h4>
                <button
                  onClick={handleStartNewTemplate}
                  className="px-4 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 shadow-sm border-2 border-green-700"
                >
                  ➕ Create New Template
                </button>
              </div>
              <div className="space-y-2">
                {Object.entries(templates).map(([key, template]) => (
                  <div key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                    <div>
                      <span className="font-medium">{template.name}</span>
                      <div className="text-sm text-gray-600">
                        {Object.keys(template.dimensions).length} dimensions
                        {template.isUserCreated ? (
                          <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-600 rounded">Custom</span>
                        ) : (
                          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded">Built-in</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditTemplate(key)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                      >
                        Edit
                      </button>
                      {template.isUserCreated && (
                        <button
                          onClick={() => handleDeleteTemplate(key)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Template Editor */}
            <div>
              {editingTemplate ? (
                <div>
                  <h4 className="font-medium text-gray-700 mb-3">
                    {creatingNewTemplate ? 'Create New Template' : `Edit Template: ${editingTemplate.name}`}
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Template Name
                      </label>
                      <input
                        type="text"
                        value={editingTemplate.name}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-gray-700">
                          Dimensions
                        </label>
                        <button
                          onClick={handleAddTemplateDimension}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          + Add Dimension
                        </button>
                      </div>

                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {Object.entries(editingTemplate.dimensions).map(([key, dim]) => (
                          <div key={key} className="p-3 border border-gray-200 rounded">
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <input
                                type="text"
                                value={dim.label}
                                onChange={(e) => handleUpdateTemplateDimension(key, 'label', e.target.value)}
                                placeholder="Label"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <select
                                value={dim.unit}
                                onChange={(e) => handleUpdateTemplateDimension(key, 'unit', e.target.value)}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="cm">cm</option>
                                <option value="mm">mm</option>
                                <option value="m">m</option>
                                <option value="inches">inches</option>
                                <option value="feet">feet</option>
                                <option value="degrees">degrees</option>
                              </select>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2 mb-2">
                              <select
                                value={dim.required ? 'true' : 'false'}
                                onChange={(e) => handleUpdateTemplateDimension(key, 'required', e.target.value)}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="false">Optional</option>
                                <option value="true">Required</option>
                              </select>
                              <input
                                type="number"
                                value={dim.min || ''}
                                onChange={(e) => handleUpdateTemplateDimension(key, 'min', e.target.value)}
                                placeholder="Min"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <input
                                type="number"
                                value={dim.max || ''}
                                onChange={(e) => handleUpdateTemplateDimension(key, 'max', e.target.value)}
                                placeholder="Max"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <input
                                type="text"
                                value={dim.placeholder || ''}
                                onChange={(e) => handleUpdateTemplateDimension(key, 'placeholder', e.target.value)}
                                placeholder="Placeholder text"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <input
                                type="text"
                                value={dim.description || ''}
                                onChange={(e) => handleUpdateTemplateDimension(key, 'description', e.target.value)}
                                placeholder="Description"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                            
                            <button
                              onClick={() => handleRemoveTemplateDimension(key)}
                              className="text-red-600 text-sm hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4 border-t">
                      <button
                        onClick={creatingNewTemplate ? handleSaveNewTemplate : handleSaveTemplate}
                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        {creatingNewTemplate ? 'Create Template' : 'Save Template'}
                      </button>
                      <button
                        onClick={creatingNewTemplate ? handleCancelNewTemplate : () => setEditingTemplate(null)}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>Select a template to edit</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DimensionConfigSettings;